import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { useAuth } from '@/hooks/useAuth';
import { buildNameInitials } from '@/lib/nameInitials';
import { cn } from '@/lib/utils';

export function ClientAccountMenu() {
  const { t } = useTranslation('clients');
  const navigate = useNavigate();
  const { currentUser, logOut } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const displayName = currentUser === null ? '' : currentUser.fullName;

  function goToAccount() {
    setIsOpen(false);
    navigate(APPLICATION_ROUTES.clientPortalAccount);
  }

  function signOut() {
    setIsOpen(false);
    logOut();
  }

  useEffect(
    function closeOnEscape() {
      if (isOpen === false) {
        return;
      }
      function handleKeyDown(keyboardEvent: KeyboardEvent) {
        if (keyboardEvent.key === 'Escape') {
          setIsOpen(false);
        }
      }
      document.addEventListener('keydown', handleKeyDown);
      return function unsubscribe() {
        document.removeEventListener('keydown', handleKeyDown);
      };
    },
    [isOpen]
  );

  return (
    <div className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t('portal.accountMenuLabel')}
        onClick={function toggleOpen() {
          setIsOpen(isOpen === false);
        }}
        className="w-10 h-10 rounded-full bg-zooprimary text-zoowhite flex items-center justify-center font-bold text-xs shrink-0 hover:bg-zooprimaryhover transition-colors"
      >
        {buildNameInitials(displayName)}
      </button>

      {isOpen === true ? (
        <>
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onClick={function closeOnBackdrop() {
              setIsOpen(false);
            }}
            className="fixed inset-0 z-40 cursor-default"
          />
          <div
            role="menu"
            className="absolute right-0 top-full mt-1 z-50 w-48 bg-zoowhite border border-zooborder rounded-xl shadow-lg overflow-hidden py-1"
          >
            <button
              type="button"
              role="menuitem"
              onClick={goToAccount}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-zooindigo hover:bg-zoovpalelavender transition-colors"
            >
              <Icon name="User" className="text-zooviolet" />
              {t('portal.manageAccount')}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={signOut}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                'text-zooerrortext hover:bg-zooerrorbg'
              )}
            >
              <Icon name="LogOut" />
              {t('portal.logout')}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
